import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateSalesReturnRequest,
    CustomerRefundRequest,
    ReturnPreviewRequest,
} from '@/lib/api/sales-return-api'
import { salesReturnApi } from '@/lib/api/sales-return-api'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'
import { orderKeys } from '@/hooks/use-sales-order-query'
import {
    customerDebtKeys,
    invalidateCustomerDebt,
} from '@/hooks/use-customer-debt-query'

// M-44 — trả hàng đơn đã giao + phiếu hoàn tiền.

const errorToast = (fallback: string) => (err: unknown) => {
    const payload = formatApiError(err, fallback)
    toast.error(payload.title, { description: payload.description })
}

/** Dòng hàng, lô còn trả được và thông tin tiền cho popup trả hàng. */
export function useOrderReturnable(orderId: string, enabled: boolean) {
    return useQuery({
        queryKey: orderKeys.returnable(orderId),
        queryFn: async () => (await salesReturnApi.returnable(orderId)).data,
        enabled: Boolean(orderId) && enabled,
        staleTime: 0,
    })
}

/** Lịch sử phiếu trả hàng của đơn (cũ trước). */
export function useOrderReturns(orderId: string, enabled = true) {
    return useQuery({
        queryKey: orderKeys.returns(orderId),
        queryFn: async () => (await salesReturnApi.list(orderId)).data ?? [],
        enabled: Boolean(orderId) && enabled,
    })
}

/** Xem trước giá trị trả + ảnh hưởng công nợ; chỉ gọi khi có dòng trả. */
export function useReturnPreview(
    orderId: string,
    items: ReturnPreviewRequest['items'],
    enabled: boolean,
    feeAmount = 0,
) {
    return useQuery({
        queryKey: orderKeys.returnPreview(orderId, { items, feeAmount }),
        queryFn: async () =>
            (
                await salesReturnApi.preview(orderId, {
                    items,
                    fee_amount: feeAmount,
                })
            ).data,
        enabled: Boolean(orderId) && enabled && items.length > 0,
        placeholderData: (prev) => prev,
    })
}

export function useCreateSalesReturn() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            orderId,
            data,
        }: {
            orderId: string
            data: CreateSalesReturnRequest
        }) => salesReturnApi.create(orderId, data),
        onSuccess: (_res, { orderId }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(orderId) })
            void qc.invalidateQueries({ queryKey: orderKeys.returns(orderId) })
            void qc.invalidateQueries({
                queryKey: orderKeys.returnable(orderId),
            })
            void qc.invalidateQueries({ queryKey: orderKeys.payments(orderId) })
            void qc.invalidateQueries({ queryKey: customerDebtKeys.all })
            toast.success(m.sales_return_toast_success())
        },
        onError: errorToast(m.sales_return_toast_error()),
    })
}

/** Hoàn tiền dư của khách từ tab công nợ (không gắn đơn). */
export function useCustomerRefund(customerId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CustomerRefundRequest) =>
            salesReturnApi.refundCustomer(customerId, data),
        onSuccess: () => {
            invalidateCustomerDebt(qc, customerId)
            toast.success(m.customer_refund_toast_success())
        },
        onError: errorToast(m.customer_refund_toast_error()),
    })
}
